//M10-11344 Po related design changes
import { Autocomplete, Button } from "src/components";
import { RootState } from "src/context/store";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { poActions } from "src/context/po-slice";
import { generatePo, getCompanyList, getStateList } from "src/services/po-api";
import { getCounterTypes } from "src/services/common-api";
import { usePoHandlers } from "./PoController";
import MultiAutocompleteAsync from "src/components/ui/MultiAutocompleteAsync";
import { useToast } from "src/hooks/useToast";
import FormSuccess from "../../container/FormSuccess";
import { DateTimeInput } from "../../components/DateTimeInput"; //M10-12019
import Loader from "../../components/Loader";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import { KAM_MODULE_CODE, PO_VALIDATION_PRODUCT_CONFIG_MISSING, SO_TYPE_OPTIONS, SO_TYPE_SAO_CN } from "./po.constants";
import PoConfigValidationModal, { PoConfigField, PoConfigGapProduct } from "./PoConfigValidationModal"; //M10-15780

const PoGenerate = () => {
    const dispatch = useDispatch();
    const { poFiltersData, poFilters } = useSelector((state: RootState) => state.po);
    const [isGeneratingPo, setIsGeneratingPo] = useState(false);
    const [openGeneratePoModal, setOpenGeneratePoModal] = useState(false);
    const [filtersCleared, setFiltersCleared] = useState(false);
    //M10-15780 — products blocking PO generation because their product-master config is incomplete
    const [poConfigValidation, setPoConfigValidation] = useState<{
        show: boolean;
        total: number;
        products: PoConfigGapProduct[];
        message?: string;
        moduleDesc?: string | null;
        checkedFields?: PoConfigField[];
    }>({ show: false, total: 0, products: [] });

    useEffect(() => {
        const init = async () => {
            handleResetFilters();
            setFiltersCleared(true);
        };
        init();
    }, []);

    const { toast } = useToast();

    const {
        fetchStockist,
        fetchOrders,
        fetchDivision,
        fetchProduct,
        handleResetFilters,
        companyHandler,
        stateHandler,
        stockistHandler,
        orderHandler,
        divisionHandler,
        productHandler,
        //M10-12019
        soTypeHandler,
        moduleHandler,
        orderDateHandler,
        fileUploadDateHandler,
        invoiceDateHandler  //M10-13833
        //M10-12019
    } = usePoHandlers();


    const handlePoGenerate = async () => {
        //M10-12019
        const orderDateISO = poFilters.orderDate;
        const fileUploadDateISO = poFilters.fileUploadDate;

        if (orderDateISO && fileUploadDateISO) {
            const orderDate = new Date(orderDateISO);
            const fileUploadDate = new Date(fileUploadDateISO);

            // Compare timestamps in milliseconds
            if (orderDate.getTime() > fileUploadDate.getTime()) {
                toast({
                    description: "Order Date cannot be greater than File Upload Date!",
                    variant: "destructive",
                });
                return;
            }
        }

        //M10-12019
        setIsGeneratingPo(true);
        const payload = {
            company_short_code: poFilters?.company?.shortcode,
            company_code: poFilters?.company?.code,
            stockist_ids: poFilters?.stockist?.map((item: any) => item?.stockist_id)?.join(","),
            counter_codes: poFilters?.counters?.map((item: any) => item?.counter_code)?.join(","),
            state_codes: poFilters?.state?.map((item: any) => item?.state_code)?.join(","),
            division_codes: poFilters?.division?.map((item: any) => item?.company_divisioncode)?.join(","),
            order_ids: poFilters?.orders?.map((item: any) => item?.order_id_int)?.join(","),
            item_codes: poFilters?.products?.map((item: any) => item?.item_code)?.join(","),
            po_generatetype: poFilters?.soType?.code,
            module_code: poFilters?.module?.module_code ?? 1,
            //M10-12019
            order_date: poFilters?.orderDate,
            file_upload_date: poFilters?.fileUploadDate,
            invoice_date: poFilters?.invoiceDate ? `${poFilters.invoiceDate}T00:00:00.000Z` : null //M10-13833
            //M10-12019
        }

        try {
            const res = await generatePo(payload);
            //M10-15780 — the OMS blocks a run whose product master is incomplete. It answers
            // 200 with success:false (its error handler cannot carry a structured list), so this
            // branch must come before the success toast or the block would be reported as a success.
            if (res?.data?.success === false && res?.data?.validation_type === PO_VALIDATION_PRODUCT_CONFIG_MISSING) {
                setPoConfigValidation({
                    show: true,
                    total: res?.data?.total ?? 0,
                    products: res?.data?.products ?? [],
                    message: res?.data?.message,
                    moduleDesc: res?.data?.module_desc,
                    checkedFields: res?.data?.checked_fields,
                });
                return;
            }
            toast({ description: "PO generation requested successfully", variant: "default" });
            if (res?.data?.success == true) {
                handleResetFilters();
            }
        } catch (error: any) {
            toast({ description: error?.response?.data?.message || "Something went wrong", variant: "destructive" });
        } finally {
            setIsGeneratingPo(false);
        }
    }

    useEffect(() => {
        if (!poFiltersData?.company?.length) {
            getCompanyList()
                .then((res) => dispatch(poActions.setPoFiltersData({ key: "company", value: res?.data })))
                .catch((err) => console.log(err));

        }
        if (!poFiltersData?.state?.length) {
            getStateList()
                .then((res) => dispatch(poActions.setPoFiltersData({ key: "state", value: res?.data?.data })))
                .catch((err) => console.log(err));
        }
        if (!poFiltersData?.module?.length) {
            // PUC=true unlocks the full module list (Medvol/POB/PUC/KAM); default returns only 1,2
            getCounterTypes("?PUC=true")
                .then((res) => {
                    const moduleList = res?.data?.data || [];
                    dispatch(poActions.setPoFiltersData({ key: "module", value: moduleList }));
                    // Seed the default selection to module_code 1 (Medvol)
                    dispatch(poActions.setPoFilters({ key: "module", value: moduleList.find((item: any) => item?.module_code === 1) }));
                })
                .catch((err) => console.log(err));
        }
    }, [dispatch]);

     if (!filtersCleared) return <Loader />;

    //M10-12019
    const formatForDateTimeLocal = (isoString?: string | null | undefined) => {
        if (!isoString) return "";
        const date = new Date(isoString); // parse ISO UTC string
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    //M10-12019

    // Order Date / File Upload Date are stored as the literal wall-clock digits the user picked,
    // dressed up with a "Z" suffix (see DateTimeInput's buildIso) — they are NOT a real UTC
    // instant. Reading them back through Date getters (as formatForDateTimeLocal does) applies
    // the browser's local timezone offset on top, shifting the displayed time (e.g. picking 11:00
    // redisplays as 05:00 in a UTC-6 browser). Slice the string directly instead — no conversion.
    const formatLiteralDateTime = (isoString?: string | null | undefined) => {
        if (!isoString) return "";
        return isoString.slice(0, 16);
    };
    //M10-13833
    const todayDateTimeLocal = formatForDateTimeLocal(new Date().toISOString());
    const todayDateOnly = todayDateTimeLocal.slice(0, 10);
    const isHiddenSoType = poFilters?.soType?.code === 'CNSH' || poFilters?.soType?.code === 'SHPO';
    //M10-13833
    const isKamModule = Number(poFilters?.module?.module_code) === KAM_MODULE_CODE;
    return (
        <>
            <div>
                <div className="flex justify-between items-center gap-2">
                    <div className="flex gap-2 w-[85%] [@media(min-width:1580px)]:w-[85%]">
                        <Autocomplete
                            onSelectionChange={companyHandler}
                            placeholder="Company"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.company || []}
                            displayKey="name"
                            defaultValue={poFilters?.company}
                            uniqueKey="code" />
                        <Autocomplete
                            onSelectionChange={moduleHandler}
                            placeholder="Module"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.module || []}
                            displayKey="module_desc"
                            defaultValue={poFilters?.module}
                            uniqueKey="module_code"
                            // Modules 2 (POB) & 3 (PUC) not integrated yet — show but keep unselectable
                            isOptionDisabled={(item: any) => item?.module_code === 2 || item?.module_code === 3} />
                        <Autocomplete
                            onSelectionChange={soTypeHandler} //M10-12019
                            placeholder="SO Type"
                            inputClassName="w-fit"
                            suggestionList={SO_TYPE_OPTIONS}
                            displayKey="name"
                            defaultValue={poFilters?.soType}
                            uniqueKey="code"
                            // SAO-CN is the KAM settlement flow — show it always, but only allow it
                            // on module 4 (the OMS rejects any other module/company outright).
                            isOptionDisabled={(item: any) => item?.code === SO_TYPE_SAO_CN && !isKamModule} />
                        {/* M10-12019 - M10-13833*/}
                        {!isHiddenSoType && (
                            <div className="flex items-center space-x-4">
                                <div className="border border-[#E5E5E5] rounded-lg flex items-center flex-1">
                                    <DateTimeInput
                                        label="Order Date"
                                        value={formatLiteralDateTime(poFilters?.orderDate)}
                                        onChange={orderDateHandler}
                                        max={todayDateTimeLocal}
                                    />
                                </div>
                                <div className="border border-[#E5E5E5] rounded-lg flex items-center flex-1">
                                    <DateTimeInput
                                        label="File Upload Date"
                                        value={formatLiteralDateTime(poFilters?.fileUploadDate)}
                                        onChange={fileUploadDateHandler}
                                        min={formatLiteralDateTime(poFilters?.orderDate)} // prevent selecting after Order Date
                                        max={todayDateTimeLocal}
                                    />
                                </div>
                            </div>
                        )}
                        {!isHiddenSoType && (
                            <div className="flex items-center border border-[#E5E5E5] rounded-lg h-[33px] flex-1">
                                <span className="px-3 text-gray-500 whitespace-nowrap">Invoice Date:</span>
                                <input
                                    type="date"
                                    className="flex-1 h-full px-2 bg-transparent text-gray-700 outline-none"
                                    value={poFilters?.invoiceDate || ""}
                                    onChange={(e) => invoiceDateHandler(e.target.value)}
                                    max={todayDateOnly}
                                />
                            </div>
                        )}
                        {/* M10-12019 - M10-13833*/}
                    </div>
                    <div className="flex gap-2">
                        <Button disabled={isGeneratingPo} isLoading={isGeneratingPo} loaderSize="small" className="font-normal text-white bg-[#8051FF]"
                            onClick={() => {
                                //M10-12019
                                if (!poFilters?.company?.code) {
                                    toast({ description: "Please select company", variant: "destructive" });
                                    return;
                                }
                                if (!poFilters?.soType?.code) {
                                    toast({ description: "Please select SO type", variant: "destructive" });
                                    return;
                                }
                                if (poFilters?.soType?.code === SO_TYPE_SAO_CN && !isKamModule) {
                                    toast({ description: "SAO-CN PO generation is available only for the KAM module", variant: "destructive" });
                                    return;
                                }
                                setOpenGeneratePoModal(true)
                            }}
                            //M10-12019
                            module={fineGrainedPermissionsKeys.PURCHASEORDER.GENERATEPO.module}
                            action={fineGrainedPermissionsKeys.PURCHASEORDER.GENERATEPO.action}>
                            Generate PO
                        </Button>
                        <Button disabled={isGeneratingPo} onClick={handleResetFilters} className="font-normal text-black bg-[#F1F1F1]">
                            Reset
                        </Button>
                    </div>
                </div>
                {!isHiddenSoType && (
                    <div className="mt-1 w-[70%] flex items-center gap-2 [@media(min-width:1580px)]:w-[70%]">
                        <MultiAutocompleteAsync
                            placeholder="States"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.state || []}
                            controlledValue={poFilters?.state}
                            displayKey="state_name"
                            uniqueKey="state_code"
                            defaultValue={poFilters?.state}
                            onSelectionChange={stateHandler}
                            showSelectAll={true}
                        />
                        <MultiAutocompleteAsync
                            placeholder="Stockists"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.stockist || []}
                            displayKey="stockist_name"
                            uniqueKey="company_stockist_code"
                            controlledValue={poFilters?.stockist}
                            defaultValue={poFilters?.stockist}
                            isAsyncQuery={true}
                            asyncListFunction={poFilters?.company?.code ? fetchStockist : undefined}
                            onSelectionChange={stockistHandler}
                            showSelectAll={true}
                            additionalDisplayKey={true}
                            additionalDisplayKeySearch="company_stockist_code"
                            // M10-13660
                            // showSecondaryDisplayKey={true}
                            // secondaryDisplayKey="company_stockist_code"
                            tertiaryDisplayKey="city"
                            //M10-13660
                        />
                        {poFilters?.stockist?.length}
                        <MultiAutocompleteAsync
                            placeholder="Divisions"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.division || []}
                            displayKey="division_name"
                            uniqueKey="company_divisioncode"
                            defaultValue={poFilters?.division}
                            controlledValue={poFilters?.division}
                            isAsyncQuery={true}
                            asyncListFunction={poFilters?.company?.code ? fetchDivision : undefined}
                            onSelectionChange={divisionHandler}
                            showSelectAll={true}
                        />
                        {poFilters?.division?.length}


                        <MultiAutocompleteAsync
                            placeholder="Orders"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.orders || []}
                            displayKey="order_code"
                            uniqueKey="order_id_int"
                            defaultValue={poFilters?.orders}
                            controlledValue={poFilters?.orders}
                            isAsyncQuery={true}
                            asyncListFunction={poFilters?.company?.code && poFilters?.soType?.code ? fetchOrders : undefined}
                            onSelectionChange={orderHandler}
                            showSelectAll={true}
                        />
                        {poFilters?.orders?.length}

                        <MultiAutocompleteAsync
                            placeholder="Products"
                            inputClassName="w-fit"
                            suggestionList={poFiltersData?.products || []}
                            displayKey="item_code"
                            uniqueKey="item_code"
                            defaultValue={poFilters?.products}
                            controlledValue={poFilters?.products}
                            isAsyncQuery={true}
                            asyncListFunction={poFilters?.company?.code ? fetchProduct : undefined}
                            onSelectionChange={productHandler}
                            showSelectAll={true}
                            // M10-15255
                            showSecondaryDisplayKey={true}
                            secondaryDisplayKey="item_description"
                            additionalDisplayKey={true}
                            additionalDisplayKeySearch="item_description"
                            // M10-15255
                        />
                        {poFilters?.products?.length}
                    </div>
                )}
            </div>
            <FormSuccess
                onCloseModal={() => {
                    setOpenGeneratePoModal(false)
                }}
                showForm={openGeneratePoModal}
                onContinue={() => {
                    setOpenGeneratePoModal(false)
                    handlePoGenerate();
                }}
                variant=""
                popupMessage={{
                    description: `Are you sure you want to generate PO for ${poFilters?.company?.name}?`,
                    title: "Generate PO!",
                }}
                enableBackButton={true}
            />
            {/* M10-15780 */}
            <PoConfigValidationModal
                show={poConfigValidation.show}
                total={poConfigValidation.total}
                products={poConfigValidation.products}
                message={poConfigValidation.message}
                companyName={poFilters?.company?.name}
                moduleDesc={poConfigValidation.moduleDesc}
                moduleCode={Number(poFilters?.module?.module_code) || undefined}
                checkedFields={poConfigValidation.checkedFields}
                onClose={() => setPoConfigValidation({ show: false, total: 0, products: [] })}
            />
        </ >
    );
};

export default PoGenerate;
//M10-11344 Po related design changes